import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSyncStore } from '@/features/sync/stores/useSyncStore';
import { UserPreferences, UserPreferencesUpdate } from '../types/user-preferences.types';
import { UserPreferencesRepository } from '../repositories/UserPreferencesRepository';

export const useUserPreferences = () => {
  const [preferences, setPreferences] = useState<UserPreferences | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const user = useSyncStore(state => state.user);

  const [showSubmittedSuccess, setShowSubmittedSuccess] = useState(false);
  const [submittedSuccessfully, setSubmittedSuccessfully] = useState(false);

  // Create repository instance
  const repository = useMemo(() => new UserPreferencesRepository(), []);

  const loadPreferences = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError(null);

    try {
      const prefs = await repository.getUserPreferences();
      setPreferences(prefs);
    } catch (err) {
      setError('Failed to load preferences');
      console.error('Error loading preferences:', err);
    } finally {
      setLoading(false);
    }
  }, [repository, user]);

  const updatePreferences = useCallback(
    async (updates: UserPreferencesUpdate) => {
      setSaving(true);
      setError(null);

      try {
        // Validate required fields
        if (!updates.displayName || !updates.subdomain) {
          throw new Error('Display name and subdomain are required');
        }

        // Check subdomain availability before saving
        const isAvailable = await repository.checkSubdomainAvailability(updates.subdomain);
        if (!isAvailable && updates.subdomain !== preferences?.subdomain) {
          throw new Error('Subdomain is already taken or reserved');
        }

        const updatedPrefs = await repository.updateUserPreferences(updates);
        setPreferences(updatedPrefs);
        setSubmittedSuccessfully(true);
        return updatedPrefs;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to save preferences';
        setError(errorMessage);
        console.error('Error saving preferences:', err);
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [repository, preferences?.subdomain]
  );

  const deletePreferences = useCallback(async () => {
    setDeleting(true);
    setError(null);

    try {
      await repository.deleteUserPreferences();
      setPreferences(null);
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete preferences';
      setError(errorMessage);
      console.error('Error deleting preferences:', err);
      throw err;
    } finally {
      setDeleting(false);
    }
  }, [repository]);

  const checkSubdomainAvailability = useCallback(
    async (subdomain: string) => {
      try {
        if (subdomain === preferences?.subdomain) {
          return true;
        }
        return await repository.checkSubdomainAvailability(subdomain);
      } catch (err) {
        console.error('Error checking subdomain availability:', err);
        return false;
      }
    },
    [repository, preferences]
  );

  useEffect(() => {
    void loadPreferences();
  }, [loadPreferences]);

  useEffect(() => {
    let timeout: NodeJS.Timeout | null = null;
    if (submittedSuccessfully) {
      setShowSubmittedSuccess(true);
      timeout = setTimeout(() => {
        setShowSubmittedSuccess(false);
        setSubmittedSuccessfully(false);
      }, 3000);
    }
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [submittedSuccessfully]);

  return {
    preferences,
    loading,
    saving,
    deleting,
    error,
    updatePreferences,
    deletePreferences,
    checkSubdomainAvailability,
    reload: loadPreferences,
    clearError: () => {
      setError(null);
    },
    showSubmittedSuccess,
  };
};
